import { z } from "@reflag/shared/utils/zod";

import { APIResponse } from "./api";
import { CreateFeatureArgsType } from "./featureAPI";
import { CustomRule, FeatureTargetingMode } from "./featureTargetingAPI";
import { CreateSegmentArgsType } from "./segmentAPI";

export const ldImportSchema = z
  .object({
    ldApiToken: z.string(),
    ldProjectId: z.string(),
    euRegion: z.boolean().default(false),
    dryRun: z.boolean().default(true),
  })
  .strict();

export type ldImportSchemaType = z.input<typeof ldImportSchema>;

export const flagsmithExportSchema = z
  .object({
    flagsmithApiToken: z.string().trim().min(1),
  })
  .strict();

export type FlagsmithExportSchemaType = z.input<typeof flagsmithExportSchema>;

export type FlagsmithExport = {
  format: "reflag-flagsmith-export";
  version: 2;
  exportedAt: string;
  source: {
    baseUrl: string;
    projectCount: number;
  };
  projects: unknown[];
};

// Import result types - these are only used for type inference, not validation

export type SkipReason =
  | "multivariate"
  | "rules_serve_false"
  | "already_exists";

export type SkippedFlag = {
  key: string;
  name: string;
  reason: SkipReason;
};

export type SkippedSegment = {
  name: string;
  reason: SkipReason;
};

// Import notes for flags and segments
export type FlagNote =
  | "operator_approximated" // Some operators were approximated (e.g., startsWith → CONTAINS)
  | "prerequisite_not_found" // A prerequisite flag was not found (may be multivariate or skipped)
  | "unknown_context_kind_ignored"; // Flag has targets with unsupported context kinds

export type SegmentNote =
  | "rollout_not_supported" // Segment has percentage-based rollouts which are not supported
  | "operator_approximated" // Some operators were approximated (e.g., startsWith → CONTAINS)
  | "unknown_context_kind_ignored"; // Segment has contexts with unsupported context kinds

export type ImportFlagTargetingVersion = {
  environmentName: string;
  targetingMode: FeatureTargetingMode;
  segmentIds: string[];
  companyIds: string[];
  userIds: string[];
  customRules: CustomRule[];
};

export type ImportedFlag = {
  flag: Pick<
    CreateFeatureArgsType,
    "source" | "name" | "description" | "key" | "secret" | "isPermanent"
  >;
  targeting: {
    versions: ImportFlagTargetingVersion[];
  };
  notes?: FlagNote[];
};

export type ImportedSegment = {
  segment: CreateSegmentArgsType;
  notes?: SegmentNote[];
};

export type EnvironmentMapping = {
  ldEnvName: string;
  reflagEnvName: string | null; // null means the environment will be created
};

export type ImportResult = {
  flags: ImportedFlag[];
  segments: ImportedSegment[];
  skippedFlags: SkippedFlag[];
  skippedSegments: SkippedSegment[];
  environmentMapping: EnvironmentMapping[];
};

export type ImportResponse = {
  dryRun: boolean;
  import: ImportResult;
  created?: {
    featureIds: string[];
    segmentIds: string[];
    environmentIds: string[];
  };
};

export interface ImportAPI {
  "/apps/:appId/import/launchdarkly": {
    POST: {
      params: { appId: string };
      body: ldImportSchemaType;
      response: APIResponse<ImportResponse>;
    };
  };
  "/apps/:appId/import/flagsmith/export": {
    POST: {
      params: { appId: string };
      body: FlagsmithExportSchemaType;
      response: APIResponse<FlagsmithExport>;
    };
  };
}
